import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Landing from "@/pages/Landing";
import Feed from "@/pages/Feed";
import Work from "@/pages/Work";
import Mobility from "@/pages/Mobility";
import Chat from "@/pages/Chat";
import Profile from "@/pages/Profile";
import Driver from "@/pages/Driver";
import Admin from "@/pages/Admin";
import Investors from "@/pages/Investors";
import Ads from "@/pages/Ads";
import AdsManager from "@/pages/AdsManager";
import AdsPixels from "@/pages/AdsPixels";
import Autonomos from "@/pages/Autonomos";
import Services from "@/pages/Services";
import Meetings from "@/pages/Meetings";
import UserProfile from "@/pages/UserProfile";
import LiveMap from "@/pages/LiveMap";
import WalletPage from "@/pages/WalletPage";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ForgotPassword from "@/pages/ForgotPassword";
import Pages from "@/pages/Pages";
import PageDetail from "@/pages/PageDetail";
import PageAdmin from "@/pages/PageAdmin";
import NotFound from "@/pages/NotFound";
import NotificationsSettings from "@/pages/NotificationsSettings";
import Notifications from "@/pages/Notifications";
import SearchPage from "@/pages/SearchPage";
import Reels from "@/pages/Reels";
import MusicReels from "@/pages/MusicReels";
import TagPage from "@/pages/TagPage";
import PostDetail from "@/pages/PostDetail";
import News from "@/pages/News";
import AppShell from "@/components/AppShell";
import ErrorBoundary from "@/components/ErrorBoundary";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  if (!user) return <Navigate to="/" replace />;
  return <AppShell><ErrorBoundary>{children}</ErrorBoundary></AppShell>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
      <Route path="/work" element={<ProtectedRoute><Work /></ProtectedRoute>} />
      <Route path="/autonomos" element={<ProtectedRoute><Autonomos /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute><Services /></ProtectedRoute>} />
      <Route path="/meetings" element={<ProtectedRoute><Meetings /></ProtectedRoute>} />
      <Route path="/user/:userId" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
      <Route path="/livemap" element={<ProtectedRoute><LiveMap /></ProtectedRoute>} />
      <Route path="/mobility" element={<ProtectedRoute><Mobility /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
      <Route path="/chat/:otherId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/driver" element={<ProtectedRoute><Driver /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/investors" element={<ProtectedRoute><Investors /></ProtectedRoute>} />
      <Route path="/ads" element={<ProtectedRoute><Ads /></ProtectedRoute>} />
      <Route path="/ads-manager" element={<ProtectedRoute><AdsManager /></ProtectedRoute>} />
      <Route path="/ads-manager/pixels" element={<ProtectedRoute><AdsPixels /></ProtectedRoute>} />
      <Route path="/pages" element={<ProtectedRoute><Pages /></ProtectedRoute>} />
      <Route path="/pages/:pageId" element={<ProtectedRoute><PageDetail /></ProtectedRoute>} />
      <Route path="/pages/:pageId/manage" element={<ProtectedRoute><PageAdmin /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/settings/notifications" element={<ProtectedRoute><NotificationsSettings /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      <Route path="/reels" element={<ProtectedRoute><Reels /></ProtectedRoute>} />
      <Route path="/music/:key" element={<ProtectedRoute><MusicReels /></ProtectedRoute>} />
      <Route path="/tag/:tag" element={<ProtectedRoute><TagPage /></ProtectedRoute>} />
      <Route path="/post/:postId" element={<ProtectedRoute><PostDetail /></ProtectedRoute>} />
      <Route path="/news" element={<ProtectedRoute><News /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster richColors position="top-center" />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
